/**
 * Retry policy for appointment pulls and eligibility checks. Single source of
 * truth for both backend (the pull's re-collection ladder, the sweeps' retry
 * logic) and frontend (failure slideout display).
 */

/** Number of automated retries after the initial attempt — the hourly pull's
 *  re-collection ladder (`classifyRuns`) for a failed Revolution run. A
 *  vision check is never app-retried: the lane's attempt ladder is its whole
 *  retry budget. */
export const MAX_RETRIES = 3;

/** Total attempts including the initial one (used for "Attempt X of Y" displays). */
export const MAX_TOTAL_ATTEMPTS = MAX_RETRIES + 1;

// ── Stedi (medical 270/271) auto-retry ───────────────────────────────
// Transient payer rejections (AAA 42/79/80), network errors, and Stedi 5xx
// are retried on a backoff by eligibilitySweep's sweepStediRetries phase.
// (Stedi gets a longer budget than the pull ladder's MAX_RETRIES because a
// real payer outage can last hours.)

/** Automated Stedi retries after the initial attempt (~7 total attempts). */
export const STEDI_MAX_RETRIES = 6;

/**
 * Delay before each Stedi retry, indexed by the just-incremented retryCount:
 * 15m → 30m → 1h → 2h → 4h (the last value caps). Spans ~8h end-to-end —
 * aligned with Stedi's "retry with backoff for up to ~8h" guidance for AAA-42
 * ("unable to respond at current time") payer-side conditions.
 */
export const STEDI_RETRY_BACKOFF_MS = [15, 30, 60, 120, 240].map(
  (m) => m * 60 * 1000,
);

/**
 * Only retry checks whose last attempt is within this window. Keeps a first
 * deploy (or a long outage) from re-running ancient backlog, and bounds the
 * loop to recently-relevant appointments. Comfortably larger than the ~8h
 * backoff span so it never cuts off an actively-retrying check.
 */
export const STEDI_RETRY_LOOKBACK_MS = 24 * 60 * 60 * 1000;

/** Backoff delay before the next retry, given the just-incremented retryCount (1..N). */
export function stediRetryDelayMs(retryCount: number): number {
  const i = Math.min(
    Math.max(retryCount - 1, 0),
    STEDI_RETRY_BACKOFF_MS.length - 1,
  );
  return STEDI_RETRY_BACKOFF_MS[i];
}
